import { Box } from "@chakra-ui/react/box";
import { Flex } from "@chakra-ui/react/flex";
import { useBreakpointValue } from "@chakra-ui/react/hooks";
import { HStack } from "@chakra-ui/react/stack";
import MerchantSwitch from "components/merchant-switch/desktop";
import NavigationBar from "components/navigation-bar";
import { BrowserNotificationEvent } from "entities/ISocketArgs";
import {
  useCurrentAgent,
  useUnreadCounts,
} from "hooks/queries/use-current-agent";
import { useInboxesRealtimeUpdates } from "hooks/queries/use-inboxes";
import { useMerchant } from "hooks/queries/use-settings-merchant";
import { useUnreadCountsRealtimeUpdates } from "hooks/queries/use-unread-counts-realtime";
import { useWebSocket } from "hooks/use-socket";
import React, { useEffect } from "react";
import { useAppSelector } from "redux/hooks";
import {
  sendBrowserNotification,
  updateBadge,
} from "util/browser_notifications";
import PageBanner from "./PageBanner";

interface PrivatePageWrapperProps {
  children: React.ReactNode;
}

const PrivatePageWrapper = ({ children }: PrivatePageWrapperProps) => {
  const isBaseSize = useBreakpointValue(
    { base: true, md: false },
    { ssr: false }
  );
  const { data: merchant } = useMerchant();
  const { addEventHandler, removeEventHandler } = useWebSocket();
  const { data: currentAgent } = useCurrentAgent();
  const { data: unreadCounts = [] } = useUnreadCounts();

  useUnreadCountsRealtimeUpdates();
  useInboxesRealtimeUpdates();

  useEffect(() => {
    const unreadCount = unreadCounts.find(
      (uc) => uc.merchant_id === merchant.id
    );

    updateBadge(unreadCount?.shared || 0);
  }, [unreadCounts]);

  const handleBrowserNotification = (args: BrowserNotificationEvent) => {
    if (currentAgent?.id === args.notification.agent_id) {
      sendBrowserNotification(
        args.notification.title,
        args.notification.body,
        args.notification.deep_link_url
      );
    }
  };

  useEffect(() => {
    addEventHandler("browser_notification", handleBrowserNotification);

    return () => {
      removeEventHandler("browser_notification", handleBrowserNotification);
    };
  }, [addEventHandler, removeEventHandler, handleBrowserNotification]);

  return (
    <>
      <PageBanner />
      {!isBaseSize ? (
        <Flex h="100%" maxHeight="100%">
          <HStack
            flexGrow={0}
            flexBasis="1px"
            flexShrink={0}
            h="100%"
            minHeight="100%"
            gap={0}
          >
            <MerchantSwitch />
            <NavigationBar />
          </HStack>
          <Flex
            flexGrow={1}
            flexBasis="1px"
            flexShrink={0}
            h="100%"
            minWidth={0}
          >
            {children}
          </Flex>
        </Flex>
      ) : (
        <Flex h="100%" w="100%" maxHeight="100%" direction="column">
          <Flex
            w="100%"
            flexDirection="column"
            flexGrow={1}
            flexBasis="1px"
            flexShrink={0}
            overflow="hidden"
          >
            {children}
          </Flex>
          <Box w="100%" flexGrow={0} flexBasis="1px" flexShrink={0}>
            <NavigationBar />
          </Box>
        </Flex>
      )}
    </>
  );
};

export default PrivatePageWrapper;
